/**
 * AI Proposal Summary Component
 *
 * Story BL-045.2: Governance Proposal Summarization
 *
 * Displays an AI-generated 2-3 sentence summary of a governance proposal.
 * Shows a loading skeleton while the summary is being fetched,
 * the summary text when available, or a "Summary unavailable" fallback
 * when the AI service is unreachable.
 *
 * This component is visually subordinate to the main proposal description —
 * it is an AI aid, not the authoritative content.
 *
 * @module components/governance/AiProposalSummary
 */

import { Sparkles, Info, Loader2 } from 'lucide-react';

// =============================================================================
// Props
// =============================================================================

export interface AiProposalSummaryProps {
  /** The AI-generated summary text, or null if unavailable */
  summary: string | null;
  /** Whether the summary is currently being fetched */
  isLoading: boolean;
}

// =============================================================================
// Component
// =============================================================================

/**
 * AI Proposal Summary display component.
 *
 * States:
 * - Loading: shows a skeleton/spinner placeholder
 * - Summary available: shows summary text in a subtle card with "AI Summary" label
 * - Summary unavailable: shows "Summary unavailable" muted text with info icon
 */
export function AiProposalSummary({ summary, isLoading }: AiProposalSummaryProps) {
  // Loading state
  if (isLoading) {
    return (
      <div
        className="flex items-center gap-2 text-xs text-muted-foreground py-3"
        data-testid="ai-proposal-summary-loading"
      >
        <Loader2 className="w-3 h-3 animate-spin" />
        <span>Generating AI summary...</span>
      </div>
    );
  }

  // Summary available
  if (summary) {
    return (
      <div
        className="bg-muted/20 rounded-lg border border-border/60 p-4 mb-4"
        data-testid="ai-proposal-summary"
      >
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-2">
          <Sparkles className="w-3 h-3" />
          <span>AI Summary</span>
        </div>
        <p
          className="text-sm text-foreground/80"
          data-testid="ai-proposal-summary-text"
        >
          {summary}
        </p>
      </div>
    );
  }

  // Summary unavailable
  return (
    <div
      className="flex items-center gap-1.5 text-xs text-muted-foreground mb-4"
      data-testid="ai-proposal-summary-unavailable"
    >
      <Info className="w-3 h-3" />
      <span>Summary unavailable</span>
    </div>
  );
}

export default AiProposalSummary;
